/**
 * Export utilities for security and audit data
 */

/**
 * Convert data to CSV format
 */
export function convertToCSV(data: any[], headers: string[]): string {
	const csvRows = [];

	// Add headers
	csvRows.push(headers.join(','));

	// Add data rows
	for (const row of data) {
		const values = headers.map(header => {
			const value = row[header];
			// Handle nested objects and arrays
			if (typeof value === 'object' && value !== null) {
				return `"${JSON.stringify(value).replace(/"/g, '""')}"`;
			}
			// Escape quotes and wrap in quotes if contains comma
			const escaped = String(value || '').replace(/"/g, '""');
			return escaped.includes(',') ? `"${escaped}"` : escaped;
		});
		csvRows.push(values.join(','));
	}

	return csvRows.join('\n');
}

/**
 * Download data as CSV file
 */
export function downloadCSV(data: any[], filename: string, headers: string[]): void {
	const csv = convertToCSV(data, headers);
	const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
	const link = document.createElement('a');

	if (link.download !== undefined) {
		const url = URL.createObjectURL(blob);
		link.setAttribute('href', url);
		link.setAttribute('download', filename);
		link.style.visibility = 'hidden';
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);
	}
}

/**
 * Download security events as CSV
 */
export function exportSecurityEvents(events: any[]): void {
	const headers = [
		'timestamp',
		'type',
		'severity',
		'userEmail',
		'userName',
		'userRole',
		'description',
		'ipAddress',
		'userAgent',
	];

	const exportData = events.map(event => ({
		timestamp: event.timestamp.toISOString(),
		type: event.type,
		severity: event.severity,
		userEmail: event.user.email,
		userName: event.user.name,
		userRole: event.user.role,
		description: event.description,
		ipAddress: event.metadata?.ip || '',
		userAgent: event.metadata?.userAgent || '',
	}));

	const filename = `security-events-${new Date().toISOString().split('T')[0]}.csv`;
	downloadCSV(exportData, filename, headers);
}

/**
 * Download audit logs as CSV
 */
export function exportAuditLogs(logs: any[]): void {
	const headers = [
		'timestamp',
		'actorEmail',
		'actorName',
		'actorRole',
		'targetEmail',
		'targetName',
		'action',
		'description',
		'success',
		'riskLevel',
		'ipAddress',
		'reason',
		'beforeData',
		'afterData',
	];

	const exportData = logs.map(log => ({
		timestamp: log.timestamp.toISOString(),
		actorEmail: log.actorEmail,
		actorName: log.actorName,
		actorRole: log.actorRole,
		targetEmail: log.targetEmail,
		targetName: log.targetName,
		action: log.action,
		description: log.description,
		success: log.success ? 'Success' : 'Failed',
		riskLevel: log.metadata?.riskLevel || '',
		ipAddress: log.metadata?.ipAddress || '',
		reason: log.metadata?.reason || '',
		beforeData: log.beforeData ? JSON.stringify(log.beforeData) : '',
		afterData: log.afterData ? JSON.stringify(log.afterData) : '',
	}));

	const filename = `audit-logs-${new Date().toISOString().split('T')[0]}.csv`;
	downloadCSV(exportData, filename, headers);
}

/**
 * Download user data as CSV
 */
export function exportUserData(users: any[]): void {
	const headers = ['email', 'name', 'roles', 'status', 'lastLoginAt', 'createdAt', 'updatedAt'];

	const exportData = users.map(user => ({
		email: user.email,
		name: user.name,
		roles: Array.isArray(user.roles) ? user.roles.join(';') : user.roles,
		status: user.status,
		lastLoginAt: user.lastLoginAt ? new Date(user.lastLoginAt).toISOString() : '',
		createdAt: user.createdAt ? new Date(user.createdAt).toISOString() : '',
		updatedAt: user.updatedAt ? new Date(user.updatedAt).toISOString() : '',
	}));

	const filename = `users-export-${new Date().toISOString().split('T')[0]}.csv`;
	downloadCSV(exportData, filename, headers);
}
